import styled from "@emotion/styled";
import {
  Link,
  MediaQueries,
  ParagraphVariantSizes,
  Spacer,
  Spacings,
} from "@polestar/component-warehouse-react";
import {
  isHeading,
  isLink,
  isList,
  isListItem,
  isParagraph,
  renderRule,
  type LinkType,
  type MetaEntry,
  type Span,
} from "datocms-structured-text-utils";
import React, { type ComponentProps } from "react";
import { renderNodeRule } from "react-datocms";

import {
  ContentHeading,
  ContentOrderedList,
  ContentParagraph,
  ContentUnorderedList,
} from "..";

import { InlineDocumentBody } from "~/routes/Document/components/InlineDocumentBody";

const StyledDiv = styled.div`
  ${MediaQueries.tablet} {
    margin-left: ${Spacings.medium};
  }
`;

const listRenderRule = renderRule(isList, ({ node, children, key }) => {
  if (node.style === "bulleted") {
    return <ContentUnorderedList key={key}>{children}</ContentUnorderedList>;
  }

  return (
    <ContentOrderedList key={key} spacing={Spacings.large}>
      {children}
    </ContentOrderedList>
  );
});

const listItemRenderRule = renderNodeRule(isListItem, ({ children, key }) => {
  return <React.Fragment key={key}>{children}</React.Fragment>;
});

const paragraphRenderRule = renderNodeRule(
  isParagraph,
  ({ children, key, ancestors }) => {
    // Don't wrap the LegalDocumentBodyRecord in a paragraph
    if (
      children?.length &&
      (
        children as Array<
          React.ReactElement<
            ComponentProps<typeof InlineDocumentBody>,
            typeof InlineDocumentBody
          >
        >
      )[0].props.__typename === "LegalDocumentBodyRecord"
    ) {
      const { props } = children[0] as React.ReactElement<
        ComponentProps<typeof InlineDocumentBody>,
        typeof InlineDocumentBody
      >;

      // eslint-disable-next-line react/jsx-props-no-spreading
      return <InlineDocumentBody key={key} {...props} />;
    }

    const isRootParagraph = ancestors.length === 1;

    return (
      <ContentParagraph
        key={key}
        spacing={isRootParagraph ? Spacings.large : undefined}
      >
        {children}
      </ContentParagraph>
    );
  }
);

const headingRenderRule = renderNodeRule(
  isHeading,
  ({ children, key, node }) => {
    if (node.level === 1) {
      return (
        <ContentHeading key={key} level={2} spacing={Spacings.large}>
          {children}
        </ContentHeading>
      );
    }

    if (node.level === 2) {
      if (
        children &&
        children.length > 0 &&
        (
          children as Array<
            React.ReactElement<
              ComponentProps<typeof InlineDocumentBody>,
              typeof InlineDocumentBody
            >
          >
        )[0].props.__typename === "LegalDocumentAccordionRecord"
      ) {
        return (
          <React.Fragment key={key}>
            <div>{children}</div>
            <Spacer spacing={Spacings.large} />
          </React.Fragment>
        );
      }

      return (
        <ContentHeading
          key={key}
          level={3}
          spacing={Spacings.large}
          variant={ParagraphVariantSizes.medium}
        >
          {children}
        </ContentHeading>
      );
    }

    if (node.level === 3) {
      if (
        children &&
        children.length > 0 &&
        (
          children as Array<
            React.ReactElement<
              ComponentProps<typeof InlineDocumentBody>,
              typeof InlineDocumentBody
            >
          >
        )[0].props.__typename === "LegalDocumentAccordionRecord"
      ) {
        return (
          <React.Fragment key={key}>
            <StyledDiv>{children}</StyledDiv>
            <Spacer spacing={Spacings.large} />
          </React.Fragment>
        );
      }

      return (
        <ContentHeading
          key={key}
          level={3}
          spacing={Spacings.large}
          variant={ParagraphVariantSizes.medium}
        >
          {children}
        </ContentHeading>
      );
    }

    return (
      <ContentHeading key={key} level={node.level} spacing={Spacings.large}>
        {children}
      </ContentHeading>
    );
  }
);

interface LinkNode {
  type: LinkType;
  url: string;
  meta?: MetaEntry[];
  children: Span[];
}

const removeDuplicatesFromArray = (array: string[]) => {
  return [...new Set(array)];
};

function getTargetValue(node: LinkNode) {
  return node.meta?.find((attribute) => {
    return attribute.id === "target";
  })?.value;
}

function getRelValue(node: LinkNode, target?: string) {
  const relValue = node.meta?.find((attribute) => {
    return attribute.id === "rel";
  })?.value;

  if (!target?.includes("_blank")) return relValue;

  if (relValue)
    return removeDuplicatesFromArray(
      `${relValue} noopener noreferrer`.split(" ")
    ).join(" ");

  return "noopener noreferrer";
}

const linkRenderRule = renderNodeRule(isLink, ({ children, key, node }) => {
  const target = getTargetValue(node);
  const rel = getRelValue(node, target);

  return (
    <Link key={key} target={target} rel={rel} href={node.url}>
      {children}
    </Link>
  );
});

const defaultStructuredTextNodeRules = [
  listRenderRule,
  listItemRenderRule,
  paragraphRenderRule,
  headingRenderRule,
  linkRenderRule,
];

export default defaultStructuredTextNodeRules;

export {
  headingRenderRule,
  linkRenderRule,
  listItemRenderRule,
  listRenderRule,
  paragraphRenderRule,
};
